import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import styles from 'components/ProfileEdit/InterviewEditTab/InterviewTab/InterviewTab.module.scss';
import { convertToOptions, convertToValue } from 'helpers/SelectUtils';
import cn from 'classnames';
import Select from 'components/common/Form/Select';
import { NO_COMPANY_STATUSES, TranslateEducation, TranslateEmployment } from 'constants/Experience';
import Input from 'components/common/Form/Input';
import YearsOfExperience from 'constants/YearsOfExperience';
import OptionSlider from 'components/common/Form/OptionSlider/OptionSlider';

const ExperienceInfo = ({ id, values, errors, onChangeField, containerRef, isModal }) => {
    const { t } = useTranslation();
    const { education, employmentStatus, company, occupation, workExperience } = values;
    const showCompanyField = employmentStatus && !NO_COMPANY_STATUSES.includes(employmentStatus);

    const EDUCATIONS = convertToOptions(TranslateEducation(t));
    const EMPLOYMENTS = convertToOptions(TranslateEmployment(t));

    return (
        <div id={id} ref={containerRef} className={styles.wrapper}>
            <div className={cn({ [styles.grid]: !isModal })}>
                <div className={styles.col}>
                    {!isModal && <span className={styles.title}>{t(`profile:text.experience`)}</span>}
                </div>
                <div className={cn(styles.col, { [styles.modal]: isModal })}>
                    <div className={styles.row}>
                        <Select
                            fullWidth
                            label={t(`profile:text.educationLabel`)}
                            name='education'
                            placeholder={t(`profile:text.educationPlaceholder`)}
                            options={EDUCATIONS}
                            value={EDUCATIONS.find((el) => el.value === education)}
                            error={t(errors.education)}
                            onChangeField={onChangeField}
                        />
                    </div>
                    <div className={styles.row}>
                        <Select
                            name='employmentStatus'
                            label={t('welcome:form.labels.employment')}
                            placeholder={t('welcome:form.placeholders.employment')}
                            options={EMPLOYMENTS}
                            value={convertToValue(employmentStatus, TranslateEmployment(t))}
                            onChangeField={onChangeField}
                            error={t(errors.employmentStatus)}
                            className={styles.wrapper}
                        />
                    </div>
                    <div className={cn(styles.row)}>
                        {showCompanyField && (
                            <>
                                <div className={styles.col}>
                                    <Input
                                        name='company'
                                        label={t('welcome:form.labels.company')}
                                        placeholder={t('welcome:form.placeholders.company')}
                                        value={company}
                                        onChangeField={onChangeField}
                                        error={t(errors.company)}
                                    />
                                </div>
                                <div className={styles.col}>
                                    <Input
                                        name='occupation'
                                        label={t('welcome:form.labels.occupation')}
                                        placeholder={t('welcome:form.placeholders.occupation')}
                                        value={occupation}
                                        onChangeField={onChangeField}
                                        error={t(errors.occupation)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                    <div className={cn(styles.row, styles.topMargin)}>
                        <OptionSlider
                            options={YearsOfExperience}
                            className={styles.experience}
                            label={t('welcome:form.labels.experience')}
                            width={37}
                            onChange={(option) => onChangeField({ target: { name: 'workExperience', value: option } })}
                            value={workExperience}
                            error={t(errors.workExperience)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

ExperienceInfo.propTypes = {
    id: PropTypes.string,
    values: PropTypes.object,
    errors: PropTypes.object,
    onChangeField: PropTypes.func,
};

ExperienceInfo.defaultProps = {
    id: null,
    values: {},
    errors: {},
    onChangeField: (f) => f,
};

export default forwardRef((props, ref) => <ExperienceInfo {...props} containerRef={ref} />);
